import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  api, ErroApi,
  type Agendamento, type Bloqueio, type EstadoAgendamento,
  type Grade, type Lista, type Profissional, type Recurso,
} from '../lib/api'
import { useSessao } from '../lib/sessao'
import { NovoAgendamento } from './NovoAgendamento'
import { NovoBloqueio } from './NovoBloqueio'
import { PainelEspera, QuemCabeAqui } from './ListaEspera'
import { ReceberAtendimento } from './ReceberAtendimento'
import './agenda.css'

/**
 * A grade.
 *
 * Três decisões que valem mais que o visual:
 *
 * 1. **O card é acromático.** Fundo neutro, cor só na barra de estado.
 *    Numa grade com 200 cards coloridos por decoração, o card que
 *    precisa de atenção não aparece — e a recepção descobre a falta
 *    quando o paciente não chega.
 *
 * 2. **Recortar e colar antes de arrastar.** Quem só implementa
 *    arrastar deixa de fora quem usa teclado, e refazer depois custa
 *    três vezes mais (WCAG 2.5.7). Aqui move-se com Enter/Espaço,
 *    setas e Enter de novo — o arrastar vem depois, por cima disso.
 *
 * 3. **Uma requisição por janela, nunca por card.** A grade recebe a
 *    semana inteira de uma vez; 400 agendamentos não podem virar 400
 *    chamadas.
 */

const ESTADOS: Record<EstadoAgendamento, { rotulo: string; classe: string }> = {
  AGENDADO:           { rotulo: 'Agendado',       classe: 'e-agendado' },
  CONFIRMADO:         { rotulo: 'Confirmado',     classe: 'e-confirmado' },
  CHEGOU:             { rotulo: 'Na recepção',    classe: 'e-recepcao' },
  ATENDENDO:          { rotulo: 'Em atendimento', classe: 'e-atendendo' },
  ATENDIDO:           { rotulo: 'Concluído',      classe: 'e-concluido' },
  FALTOU:             { rotulo: 'Faltou',         classe: 'e-faltou' },
  CANCELADO_PACIENTE: { rotulo: 'Cancelado',      classe: 'e-cancelado' },
  CANCELADO_CLINICA:  { rotulo: 'Cancelado',      classe: 'e-cancelado' },
  REMARCADO:          { rotulo: 'Remarcado',      classe: 'e-cancelado' },
}

/**
 * O que se pode fazer a partir de cada estado — espelha o gatilho de
 * `db/0007_agenda.sql:240`.
 *
 * ⚠️ Faltavam `CANCELADO_CLINICA` e `REMARCADO`, que o banco SEMPRE
 * aceitou. O efeito: toda desmarcação feita PELA CLÍNICA era gravada
 * como cancelamento do PACIENTE. A taxa de falta — que é o número que
 * justifica a régua de WhatsApp e que o dono usa para cobrar a equipe —
 * vinha sendo envenenada desde o primeiro dia, e não se reconstrói
 * depois.
 */
const PROXIMOS: Partial<Record<EstadoAgendamento, EstadoAgendamento[]>> = {
  AGENDADO:   ['CONFIRMADO', 'CHEGOU', 'FALTOU', 'CANCELADO_PACIENTE', 'CANCELADO_CLINICA', 'REMARCADO'],
  CONFIRMADO: ['CHEGOU', 'FALTOU', 'CANCELADO_PACIENTE', 'CANCELADO_CLINICA', 'REMARCADO'],
  CHEGOU:     ['ATENDENDO', 'FALTOU', 'CANCELADO_CLINICA'],
  ATENDENDO:  ['ATENDIDO'],
}

/**
 * O rótulo do BOTÃO é diferente do rótulo do SELO.
 *
 * O selo responde "o que aconteceu" e cabe em duas palavras. O botão
 * responde "o que eu estou afirmando ao clicar", e aí a diferença entre
 * "o paciente cancelou" e "nós desmarcamos" é a diferença entre culpar
 * o cliente e assumir. Dois botões escritos "Cancelado" lado a lado
 * seriam pior que um só.
 */
const ACAO: Partial<Record<EstadoAgendamento, string>> = {
  CANCELADO_PACIENTE: 'O paciente cancelou',
  CANCELADO_CLINICA:  'Nós desmarcamos',
  REMARCADO:          'Remarcar',
  FALTOU:             'Não veio',
}

/** Quais transições pedem motivo escrito. */
const PEDE_MOTIVO = new Set<EstadoAgendamento>([
  'CANCELADO_PACIENTE', 'CANCELADO_CLINICA', 'REMARCADO', 'FALTOU',
])

const DIA_MS = 86_400_000
const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
const chaveDia = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

type Eixo = 'recurso' | 'profissional'
type Visao = 'dia' | 'semana'
type Coluna = { id: string; nome: string; cor: string | null; sub?: string }

const DIAS_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

export function Agenda() {
  const { pode } = useSessao()
  const [dia, setDia] = useState(() => new Date())
  const [visao, setVisao] = useState<Visao>('dia')
  const [eixo, setEixo] = useState<Eixo>('recurso')
  const [grade, setGrade] = useState<Grade | null>(null)
  const [recursos, setRecursos] = useState<Recurso[]>([])
  const [profissionais, setProfissionais] = useState<Profissional[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [selecionado, setSelecionado] = useState<Agendamento | null>(null)
  const [movendo, setMovendo] = useState<Agendamento | null>(null)
  const [bloqueando, setBloqueando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const [novo, setNovo] = useState<{ inicio: Date; coluna: string } | null>(null)
  const [vendoEspera, setVendoEspera] = useState(false)
  /** Horário que vagou: abre "quem cabe aqui" com o buraco na mão. */
  const [encaixe, setEncaixe] = useState<{ inicio: string; proc: string | null } | null>(null)
  /* Quem está DESMARCADO no filtro lateral. Guardar os ocultos, e não
     os visíveis, faz um profissional recém-cadastrado aparecer sozinho
     em vez de sumir até alguém lembrar de marcá-lo. */
  const [ocultos, setOcultos] = useState<Set<string>>(() => new Set())

  const inicioDia = useMemo(() => {
    const d = new Date(dia); d.setHours(0, 0, 0, 0); return d
  }, [dia])
  const fimDia = useMemo(() => new Date(inicioDia.getTime() + DIA_MS), [inicioDia])

  /** Domingo da semana de `dia`, à meia-noite. */
  const inicioSemana = useMemo(() => {
    const d = new Date(inicioDia)
    d.setDate(d.getDate() - d.getDay())
    return d
  }, [inicioDia])

  /* Memoizado de propósito: `carregar` depende destas duas datas, e um
     objeto Date novo a cada render faria o efeito de carga disparar em
     laço infinito. */
  const [de, ate] = useMemo(
    () => visao === 'dia'
      ? [inicioDia, fimDia]
      : [inicioSemana, new Date(inicioSemana.getTime() + 7 * DIA_MS)],
    [visao, inicioDia, fimDia, inicioSemana],
  )

  /* ── carga ───────────────────────────────────────────────────── */
  const carregar = useCallback(async (sinal?: AbortSignal) => {
    setErro(null)
    try {
      const q = new URLSearchParams({ de: de.toISOString(), ate: ate.toISOString() })
      const g = await api<Grade>(`/v1/agenda?${q}`, sinal ? { sinal } : {})
      setGrade(g)
    } catch (e) {
      if ((e as Error).name === 'AbortError') return
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar a agenda.')
    } finally {
      setCarregando(false)
    }
  }, [de, ate])

  useEffect(() => {
    const c = new AbortController()
    setCarregando(true)
    void carregar(c.signal)
    return () => c.abort()
  }, [carregar])

  useEffect(() => {
    void (async () => {
      try {
        const [r, p] = await Promise.all([
          api<Lista<Recurso>>('/v1/recursos?limite=100'),
          api<Lista<Profissional>>('/v1/profissionais?limite=100'),
        ])
        setRecursos(r.itens.filter((x) => x.ativo && x.agendavel))
        setProfissionais(p.itens.filter((x) => x.ativo))
      } catch { /* a grade ainda funciona sem os nomes das colunas */ }
    })()
  }, [])

  /* ── o que o filtro lateral lista ────────────────────────────── */
  /* A lista é sempre de recursos e profissionais — mesmo na visão
     semana, onde as COLUNAS são dias. É a diferença entre "que colunas
     mostrar" e "que atendimentos mostrar", e o filtro faz a segunda
     coisa nos dois modos. */
  const filtraveis = useMemo(() => ({
    recursos: recursos.map((r) => ({ id: r.id, nome: r.nome, cor: r.cor })),
    profissionais: profissionais.map((p) => ({
      id: p.id, nome: p.nome_curto ?? p.nome, cor: p.cor,
    })),
  }), [recursos, profissionais])

  /** Um agendamento passa pelo filtro se nenhum dos seus donos está oculto. */
  const visivel = useCallback((a: Agendamento): boolean => {
    if (ocultos.size === 0) return true
    const donos = a.alocacao.flatMap((x) =>
      [x.recurso_id, x.profissional_id].filter(Boolean) as string[])
    if (donos.length === 0) return true
    return donos.some((d) => !ocultos.has(d))
  }, [ocultos])

  /* ── colunas ─────────────────────────────────────────────────── */
  const colunas: Coluna[] = useMemo(() => {
    if (visao === 'semana') {
      // Sete dias, domingo a sábado. O dia corrente ganha marca no CSS.
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(inicioSemana.getTime() + i * DIA_MS)
        return {
          id: chaveDia(d),
          nome: DIAS_CURTOS[d.getDay()]!,
          sub: String(d.getDate()).padStart(2, '0'),
          cor: null,
        }
      })
    }
    const base = eixo === 'recurso' ? filtraveis.recursos : filtraveis.profissionais
    const visiveis = base.filter((c) => !ocultos.has(c.id))
    if (visiveis.length > 0) return visiveis
    // Sem cadastro (ou tudo oculto): uma coluna só, para a grade não sumir.
    return [{ id: '_todos', nome: base.length > 0 ? 'Nada selecionado' : 'Agenda', cor: null }]
  }, [visao, inicioSemana, eixo, filtraveis, ocultos])

  /** Em que coluna cada agendamento cai. */
  const colunaDe = useCallback((a: Agendamento): string => {
    // Na semana a coluna é o DIA; no dia, o recurso ou o profissional.
    if (visao === 'semana') return chaveDia(new Date(a.inicio))
    const alvo = eixo === 'recurso'
      ? a.alocacao.find((x) => x.recurso_id)?.recurso_id
      : a.alocacao.find((x) => x.profissional_id)?.profissional_id
    return alvo ?? '_todos'
  }, [visao, eixo])

  /** A cor do card: sempre a do dono, nunca a do estado. */
  const corDe = useCallback((a: Agendamento): string | null => {
    const rid = a.alocacao.find((x) => x.recurso_id)?.recurso_id
    const pid = a.alocacao.find((x) => x.profissional_id)?.profissional_id
    const dono = eixo === 'recurso'
      ? filtraveis.recursos.find((r) => r.id === rid) ?? filtraveis.profissionais.find((p) => p.id === pid)
      : filtraveis.profissionais.find((p) => p.id === pid) ?? filtraveis.recursos.find((r) => r.id === rid)
    return dono?.cor ?? null
  }, [eixo, filtraveis])

  /* ── faixa de horas visível ──────────────────────────────────── */
  // Começa em 7h e termina em 20h, mas ESTICA para caber o que existir
  // fora disso: um atendimento às 6h não pode ficar invisível.
  const [horaIni, horaFim] = useMemo(() => {
    let ini = 7, fim = 20
    for (const a of grade?.agendamentos ?? []) {
      ini = Math.min(ini, new Date(a.inicio).getHours())
      fim = Math.max(fim, new Date(a.fim).getHours() + 1)
    }
    return [ini, Math.min(fim, 24)]
  }, [grade])

  const PX_POR_MIN = 1.5
  const alturaGrade = (horaFim - horaIni) * 60 * PX_POR_MIN

  const posicao = useCallback((iso: string) => {
    const d = new Date(iso)
    return (d.getHours() * 60 + d.getMinutes() - horaIni * 60) * PX_POR_MIN
  }, [horaIni])

  const altura = useCallback((ini: string, fim: string) => {
    const min = (new Date(fim).getTime() - new Date(ini).getTime()) / 60000
    // 22px é o piso para o card continuar legível numa sessão de 15 min.
    return Math.max(min * PX_POR_MIN, 22)
  }, [])

  /* ── ações ───────────────────────────────────────────────────── */
  const mudarEstado = useCallback(async (a: Agendamento, status: EstadoAgendamento, motivo?: string) => {
    try {
      /* O `motivo` a rota SEMPRE aceitou (`agenda.ts`, maxLength 300) e
         entrava NULL em 100% dos casos, porque nenhuma tela o oferecia.
         Sem ele, "perdi 14 horas no mês" não vira "por quê". */
      await api(`/v1/agendamentos/${a.id}/estado`, {
        metodo: 'POST',
        corpo: { status, ...(motivo?.trim() ? { motivo: motivo.trim() } : {}) },
      })
      setAviso(`${a.paciente ?? 'Atendimento'}: ${ESTADOS[status].rotulo.toLowerCase()}`)
      setSelecionado(null)
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível mudar o estado.')
    }
  }, [carregar])

  const mover = useCallback(async (a: Agendamento, novoInicio: Date) => {
    const dur = new Date(a.fim).getTime() - new Date(a.inicio).getTime()
    try {
      await api(`/v1/agendamentos/${a.id}/horario`, {
        metodo: 'PATCH',
        corpo: {
          inicio: novoInicio.toISOString(),
          fim: new Date(novoInicio.getTime() + dur).toISOString(),
          versao: a.versao,
        },
      })
      setAviso(`Movido para ${novoInicio.toLocaleTimeString('pt-BR',
        { hour: '2-digit', minute: '2-digit' })}`)
      setMovendo(null)
      await carregar()
    } catch (e) {
      const msg = e instanceof ErroApi ? e.message : 'Não foi possível mover.'
      setErro(msg)
      // Conflito e desatualização precisam de dados frescos na tela:
      // insistir sobre um estado velho só repete o erro.
      if (e instanceof ErroApi && (e.eConflito || e.eDesatualizado)) {
        setMovendo(null); await carregar()
      }
    }
  }, [carregar])

  /* ── teclado: recortar, mover, colar ─────────────────────────── */
  useEffect(() => {
    if (!movendo) return
    const onKey = (ev: KeyboardEvent) => {
      const passo = ev.shiftKey ? 60 : 15
      const atual = new Date(movendo.inicio)
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        ev.preventDefault()
        const delta = (ev.key === 'ArrowDown' ? passo : -passo) * 60_000
        setMovendo({ ...movendo, inicio: new Date(atual.getTime() + delta).toISOString() })
      } else if (ev.key === 'Enter') {
        ev.preventDefault(); void mover(movendo, new Date(movendo.inicio))
      } else if (ev.key === 'Escape') {
        ev.preventDefault(); setMovendo(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [movendo, mover])

  const podeEscrever = pode('agenda:write')

  /**
   * Reabrir um horário fechado.
   *
   * Um bloqueio de férias vira N linhas independentes (o `NovoBloqueio`
   * cria uma por dia, de propósito — regra de recorrência no banco vira
   * exceção sobre exceção na primeira vez que alguém precisa cancelar
   * UMA terça). Então remover aqui remove UM dia, e a mensagem diz isso
   * antes: descobrir depois que só um dia voltou é pior que o trabalho
   * de repetir o clique.
   */
  async function removerBloqueio(b: Bloqueio) {
    if (!podeEscrever) return
    const quando = new Date(b.inicio).toLocaleDateString('pt-BR', {
      weekday: 'short', day: '2-digit', month: '2-digit',
    })
    const ok = window.confirm(
      `Reabrir "${b.titulo ?? b.tipo}" em ${quando}?\n\n`
      + 'O horário volta a aceitar marcação. Só este dia — os outros dias '
      + 'do mesmo período continuam fechados.')
    if (!ok) return
    try {
      await api(`/v1/bloqueios/${b.id}`, { metodo: 'DELETE' })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível reabrir o horário.')
    }
  }

  return (
    <div className="agenda">
      <header className="agenda__topo">
        <div className="agenda__nav">
          <button className="botao botao--fantasma botao--pequeno"
                  onClick={() => setDia(new Date(dia.getTime() - DIA_MS))}
                  aria-label="Dia anterior">←</button>
          <button className="botao botao--fantasma botao--pequeno"
                  onClick={() => setDia(new Date())}>Hoje</button>
          <button className="botao botao--fantasma botao--pequeno"
                  onClick={() => setDia(new Date(dia.getTime() + DIA_MS))}
                  aria-label="Próximo dia">→</button>
          <h1 className="agenda__data">
            {dia.toLocaleDateString('pt-BR',
              { weekday: 'long', day: '2-digit', month: 'long' })}
          </h1>
          <input type="date" className="entrada agenda__data-input"
                 value={chaveDia(dia)}
                 onChange={(e) => {
                   const [a, m, d] = e.target.value.split('-').map(Number)
                   if (a && m && d) setDia(new Date(a, m - 1, d))
                 }} />
        </div>

        {podeEscrever && (
          <button className="botao" onClick={() => {
            const d = new Date(inicioDia); d.setHours(9, 0, 0, 0)
            setNovo({ inicio: d, coluna: colunas[0]?.id ?? '_todos' })
          }}>Novo atendimento</button>
        )}

        <div className="agenda__eixo" role="group" aria-label="Período">
          <button className={`agenda__aba ${visao === 'dia' ? 'is-ativa' : ''}`}
                  onClick={() => setVisao('dia')}
                  aria-pressed={visao === 'dia'}>Dia</button>
          <button className={`agenda__aba ${visao === 'semana' ? 'is-ativa' : ''}`}
                  onClick={() => setVisao('semana')}
                  aria-pressed={visao === 'semana'}>Semana</button>
        </div>

        {/* O eixo só faz sentido no dia: na semana as colunas já são os
            dias, e o recurso vira filtro em vez de coluna. */}
        {visao === 'dia' && (
          <div className="agenda__eixo" role="group" aria-label="Ver agenda por">
            <button className={`agenda__aba ${eixo === 'recurso' ? 'is-ativa' : ''}`}
                    onClick={() => setEixo('recurso')}
                    aria-pressed={eixo === 'recurso'}>Por sala</button>
            <button className={`agenda__aba ${eixo === 'profissional' ? 'is-ativa' : ''}`}
                    onClick={() => setEixo('profissional')}
                    aria-pressed={eixo === 'profissional'}>Por profissional</button>
          </div>
        )}

        {podeEscrever && (
          <button className="botao botao--fantasma botao--pequeno"
                  onClick={() => setBloqueando(true)}>Fechar horário</button>
        )}

        <button className="botao botao--fantasma botao--pequeno"
                onClick={() => setVendoEspera(true)}>Lista de espera</button>

        <button className="botao botao--fantasma botao--pequeno agenda__imprimir"
                onClick={() => window.print()}>Imprimir</button>
      </header>

      {/* A fita de "em construção" saiu: `POST /v1/bloqueios` existe
          desde a semana da agenda e agora tem tela. O que continua
          pendente de verdade é arrastar com o mouse e a visão de mês. */}

      {erro && (
        <div className="aviso aviso--erro agenda__aviso" role="alert">
          <span>{erro}</span>
          <button className="botao botao--fantasma botao--pequeno"
                  onClick={() => { setErro(null); void carregar() }}>Recarregar</button>
        </div>
      )}

      {bloqueando && (
        <NovoBloqueio
          recursos={filtraveis.recursos}
          profissionais={filtraveis.profissionais}
          diaInicial={dia}
          aoFechar={() => setBloqueando(false)}
          aoCriar={async () => { setBloqueando(false); await carregar() }} />
      )}

      {movendo && (
        <div className="aviso aviso--atencao agenda__aviso" role="status">
          <span>
            Movendo <strong>{movendo.paciente}</strong> para{' '}
            <strong>{hhmm(movendo.inicio)}</strong>.
            {' '}Setas ajustam 15 min (Shift = 1 h), Enter confirma, Esc cancela.
          </span>
          <button className="botao botao--pequeno"
                  onClick={() => void mover(movendo, new Date(movendo.inicio))}>Confirmar</button>
          <button className="botao botao--fantasma botao--pequeno"
                  onClick={() => setMovendo(null)}>Cancelar</button>
        </div>
      )}

      {/* Mudanças de estado anunciadas para leitor de tela. */}
      <p className="oculto-visual" role="status" aria-live="polite">{aviso}</p>

      <div className="agenda__palco">
        <FiltroLateral
          recursos={filtraveis.recursos}
          profissionais={filtraveis.profissionais}
          ocultos={ocultos}
          aoAlternar={(id) => setOcultos((s) => {
            const n = new Set(s)
            n.has(id) ? n.delete(id) : n.add(id)
            return n
          })}
          aoLimpar={() => setOcultos(new Set())}
        />

      <div className="agenda__quadro rolagem-x">
        <div className="agenda__colunas"
             style={{ gridTemplateColumns: `56px repeat(${colunas.length}, minmax(${
               visao === 'semana' ? 118 : 160}px, 1fr))` }}>

          <div className="agenda__canto" />
          {colunas.map((c) => (
            <div key={c.id}
                 className={`agenda__cabecalho ${
                   visao === 'semana' && c.id === chaveDia(new Date()) ? 'is-hoje' : ''}`}>
              {c.cor && <span className="agenda__ponto" style={{ background: c.cor }} />}
              {c.nome}
              {c.sub && <strong className="agenda__cab-dia">{c.sub}</strong>}
            </div>
          ))}

          <div className="agenda__horas" style={{ height: alturaGrade }}>
            {Array.from({ length: horaFim - horaIni }, (_, i) => (
              <div key={i} className="agenda__hora" style={{ height: 60 * PX_POR_MIN }}>
                <span>{String(horaIni + i).padStart(2, '0')}:00</span>
              </div>
            ))}
          </div>

          {colunas.map((col) => (
            <div key={col.id} className="agenda__coluna" style={{ height: alturaGrade }}
                 onClick={(ev) => {
                   // Só o clique no VAZIO abre o formulário; o clique no
                   // card abre o painel e não deve fazer as duas coisas.
                   if (!podeEscrever) return
                   if ((ev.target as HTMLElement).closest('.card')) return
                   const caixa = ev.currentTarget.getBoundingClientRect()
                   const minutos = (ev.clientY - caixa.top) / PX_POR_MIN + horaIni * 60
                   // Arredonda para baixo em blocos de 15: ninguém quer
                   // marcar às 09:37 porque o clique caiu ali.
                   const bloco = Math.floor(minutos / 15) * 15
                   // Na semana, o dia vem da COLUNA clicada; no dia, do
                   // período em tela.
                   const d = visao === 'semana'
                     ? new Date(`${col.id}T00:00:00`)
                     : new Date(inicioDia)
                   d.setHours(Math.floor(bloco / 60), bloco % 60, 0, 0)
                   setNovo({ inicio: d, coluna: col.id })
                 }}>
              {Array.from({ length: horaFim - horaIni }, (_, i) => (
                <div key={i} className="agenda__linha" style={{ height: 60 * PX_POR_MIN }} />
              ))}

              {carregando && (
                <>
                  <div className="esqueleto agenda__fantasma" style={{ top: 30, height: 70 }} />
                  <div className="esqueleto agenda__fantasma" style={{ top: 140, height: 70 }} />
                </>
              )}

              {(grade?.bloqueios ?? [])
                /* O `alvo_tipo` importa tanto quanto o `alvo_id`.
                   Sem ele, no eixo "por sala" um bloqueio de
                   PROFISSIONAL não bate com coluna nenhuma e o almoço
                   do fisioterapeuta fica INVISÍVEL — a grade aceita
                   marcar em cima de quem não está lá. Na visão de
                   semana, onde a coluna é o dia, nenhum bloqueio
                   aparecia. */
                .filter((b) => {
                  if (col.id === '_todos') return true          // semana: a coluna é o dia
                  if (!b.alvo_id) return true                    // vale para a clínica inteira
                  const meuTipo = eixo === 'recurso' ? 'RECURSO' : 'PROFISSIONAL'
                  // Bloqueio do OUTRO eixo não tem coluna para morar
                  // nesta visão. Mostrá-lo em todas mentiria; escondê-lo
                  // em silêncio é o defeito de hoje. Fica no aviso da
                  // barra até a visão dele existir.
                  if (b.alvo_tipo && b.alvo_tipo !== meuTipo) return false
                  return b.alvo_id === col.id
                })
                .map((b: Bloqueio) => (
                  /* Clicável para REMOVER. `DELETE /v1/bloqueios/:id`
                     existe e é testado desde a semana da agenda, e
                     nenhuma tela chamava: fechar o horário errado era
                     definitivo, e a saída era marcar um agendamento
                     falso por cima — que é exatamente o que suja o
                     no-show e a ocupação.

                     `confirm` e não painel: é uma pergunta de uma
                     linha com resposta binária, e abrir sobreposição
                     para isso é mais clique do que a ação vale. */
                  <button key={`${b.id}-${col.id}`} type="button"
                          className="agenda__bloqueio"
                          style={{ top: posicao(b.inicio), height: altura(b.inicio, b.fim) }}
                          title={podeEscrever
                            ? `${b.titulo ?? b.tipo} — clique para reabrir este horário`
                            : (b.titulo ?? b.tipo)}
                          disabled={!podeEscrever}
                          onClick={() => { void removerBloqueio(b) }}>
                    {b.titulo ?? b.tipo}
                  </button>
                ))}

              {(grade?.agendamentos ?? [])
                .filter((a) => colunaDe(a) === col.id && visivel(a))
                .map((a) => {
                  const est = ESTADOS[a.status] ?? ESTADOS.AGENDADO
                  const emMovimento = movendo?.id === a.id
                  const iso = emMovimento ? movendo.inicio : a.inicio
                  const cor = corDe(a)
                  return (
                    <button
                      key={a.id}
                      className={`card ${est.classe} ${emMovimento ? 'is-movendo' : ''}`}
                      /*
                        A cor do DONO entra como faixa lateral, e o ESTADO
                        continua mandando no corpo do card.

                        O concorrente escolhe uma coisa só — pinta tudo
                        pela cor do profissional — e numa tela com quarenta
                        cards vira mosaico onde a falta não salta. Aqui as
                        duas perguntas têm resposta ao mesmo tempo: "de
                        quem é este horário" na faixa, "o que precisa de
                        atenção" no corpo.
                      */
                      style={{
                        top: posicao(iso), height: altura(a.inicio, a.fim),
                        ...(cor ? { borderLeftColor: cor, borderLeftWidth: 3 } : {}),
                      }}
                      onClick={() => setSelecionado(a)}
                      aria-label={`${hhmm(a.inicio)} ${a.paciente ?? 'sem paciente'}, ${est.rotulo}`}
                    >
                      <span className="card__hora">{hhmm(iso)}</span>
                      <span className="card__nome">
                        {a.paciente_social ?? a.paciente ?? 'Sem paciente'}
                      </span>
                      <span className="card__estado">{est.rotulo}</span>
                      {a.paciente_financeiro === 'VENCIDO' && (
                        <span className="card__sinal" title="Pendência financeira vencida">$</span>
                      )}
                    </button>
                  )
                })}
            </div>
          ))}
        </div>
      </div>

      </div>

      {!carregando && (grade?.agendamentos.length ?? 0) === 0 && (
        <p className="agenda__vazio">
          Nenhum atendimento {visao === 'dia' ? 'neste dia' : 'nesta semana'}.
          {recursos.length === 0 && ' Cadastre uma sala em Clínica para começar.'}
        </p>
      )}

      {/* Há atendimento, mas o filtro escondeu todos: dizer isso evita
          que alguém ache que a agenda está vazia. */}
      {!carregando && (grade?.agendamentos.length ?? 0) > 0 &&
       (grade?.agendamentos ?? []).filter(visivel).length === 0 && (
        <p className="agenda__vazio">
          Os {grade?.agendamentos.length} atendimentos deste período estão ocultos pelo filtro.{' '}
          <button className="botao botao--fantasma botao--pequeno"
                  onClick={() => setOcultos(new Set())}>Mostrar todos</button>
        </p>
      )}

      {novo && (
        <NovoAgendamento
          inicio={novo.inicio}
          recursoId={eixo === 'recurso' && novo.coluna !== '_todos' ? novo.coluna : null}
          profissionalId={eixo === 'profissional' && novo.coluna !== '_todos' ? novo.coluna : null}
          aoFechar={() => setNovo(null)}
          aoCriar={() => { setNovo(null); setAviso('Atendimento marcado'); void carregar() }}
        />
      )}

      {selecionado && (
        <PainelAgendamento
          a={selecionado}
          podeEscrever={podeEscrever}
          aoFechar={() => setSelecionado(null)}
          aoMudarEstado={(s, motivo) => void mudarEstado(selecionado, s, motivo)}
          aoMover={() => { setMovendo(selecionado); setSelecionado(null) }}
          aoVerEncaixe={() => {
            setEncaixe({
              inicio: selecionado.inicio,
              proc: selecionado.procedimento_id ?? null,
            })
            setSelecionado(null)
          }}
        />
      )}

      {vendoEspera && (
        <PainelEspera podeEditar={podeEscrever}
                      aoFechar={() => setVendoEspera(false)} />
      )}

      {encaixe && (
        <QuemCabeAqui inicio={encaixe.inicio} procedimentoId={encaixe.proc}
                      aoFechar={() => setEncaixe(null)} />
      )}
    </div>
  )
}

/* ══ filtro lateral ═══════════════════════════════════════════════ */

type Filtravel = { id: string; nome: string; cor: string | null }

/**
 * Liga e desliga profissionais e salas na grade.
 *
 * Guarda os OCULTOS, não os visíveis — assim um profissional
 * recém-cadastrado aparece sozinho, em vez de sumir até alguém lembrar
 * de marcá-lo numa lista de seleção.
 *
 * A cor ao lado de cada nome é a mesma que pinta a faixa do card. É o
 * que faz a legenda ser legenda de verdade: dá para achar os
 * atendimentos de alguém sem ler nome nenhum.
 */
function FiltroLateral({
  recursos, profissionais, ocultos, aoAlternar, aoLimpar,
}: {
  recursos: Filtravel[]
  profissionais: Filtravel[]
  ocultos: Set<string>
  aoAlternar(id: string): void
  aoLimpar(): void
}) {
  const [aberto, setAberto] = useState(false)
  if (recursos.length === 0 && profissionais.length === 0) return null

  const grupos: Array<[string, Filtravel[]]> = [
    ['Profissionais', profissionais],
    ['Salas e recursos', recursos],
  ]

  return (
    <aside className={`agfil ${aberto ? 'is-aberto' : ''}`}>
      <button className="agfil__punho" onClick={() => setAberto(!aberto)}
              aria-expanded={aberto}>
        Filtrar
        {ocultos.size > 0 && <span className="agfil__conta">{ocultos.size}</span>}
      </button>

      <div className="agfil__corpo">
        {ocultos.size > 0 && (
          <button className="botao botao--fantasma botao--pequeno agfil__limpar"
                  onClick={aoLimpar}>Mostrar todos</button>
        )}
        {grupos.map(([titulo, itens]) => itens.length === 0 ? null : (
          <div key={titulo} className="agfil__grupo">
            <h3>{titulo}</h3>
            <ul>
              {itens.map((i) => {
                const on = !ocultos.has(i.id)
                return (
                  <li key={i.id}>
                    <label className={on ? '' : 'is-off'}>
                      <input type="checkbox" checked={on}
                             onChange={() => aoAlternar(i.id)} />
                      <span className="agfil__cor"
                            style={{ background: i.cor ?? 'var(--cl-linha-forte)' }}
                            aria-hidden="true" />
                      <span className="agfil__nome">{i.nome}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </aside>
  )
}

/* ══ painel lateral ═══════════════════════════════════════════════ */

function PainelAgendamento({
  a, podeEscrever, aoFechar, aoMudarEstado, aoMover, aoVerEncaixe,
}: {
  a: Agendamento
  podeEscrever: boolean
  aoFechar(): void
  aoMudarEstado(s: EstadoAgendamento, motivo?: string): void
  aoMover(): void
  aoVerEncaixe(): void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const est = ESTADOS[a.status] ?? ESTADOS.AGENDADO
  const proximos = PROXIMOS[a.status] ?? []
  const [pedindo, setPedindo] = useState<EstadoAgendamento | null>(null)

  // Foco entra no painel ao abrir e Esc fecha: sem isso, quem usa
  // teclado fica preso atrás da grade.
  useEffect(() => {
    ref.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [aoFechar])

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel" ref={ref} tabIndex={-1}
             role="dialog" aria-modal="true" aria-label="Detalhes do atendimento">
        <header className="painel__topo">
          <span className={`selo ${
            a.status === 'FALTOU' ? 'selo--erro'
            : a.status === 'CONFIRMADO' || a.status === 'ATENDIDO' ? 'selo--ok'
            : a.status === 'CHEGOU' ? 'selo--atencao' : 'selo--neutro'}`}>
            {est.rotulo}
          </span>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>
            Fechar
          </button>
        </header>

        <h2 className="painel__nome">{a.paciente_social ?? a.paciente ?? 'Sem paciente'}</h2>
        {a.paciente_social && a.paciente && (
          <p className="ajuda">Nome de registro: {a.paciente}</p>
        )}

        <dl className="painel__dados">
          <div><dt>Horário</dt><dd>{hhmm(a.inicio)} — {hhmm(a.fim)}</dd></div>
          {a.procedimento && <div><dt>Procedimento</dt><dd>{a.procedimento}</dd></div>}
          {a.alocacao.filter((x) => x.recurso).length > 0 && (
            <div><dt>Sala</dt>
              <dd>{a.alocacao.map((x) => x.recurso).filter(Boolean).join(', ')}</dd></div>
          )}
          {a.alocacao.filter((x) => x.profissional).length > 0 && (
            <div><dt>Profissional</dt>
              <dd>{a.alocacao.map((x) => x.profissional).filter(Boolean).join(', ')}</dd></div>
          )}
          {a.paciente_telefone && (
            <div><dt>Telefone</dt>
              <dd><a href={`tel:${a.paciente_telefone}`}>{a.paciente_telefone}</a></dd></div>
          )}
          {a.observacao && <div><dt>Observação</dt><dd>{a.observacao}</dd></div>}
        </dl>

        {a.paciente_financeiro === 'VENCIDO' && (
          <div className="aviso aviso--atencao">
            Este paciente tem pendência financeira vencida.
          </div>
        )}

        {/* Horário que vagou e ainda está no futuro: oferecer o encaixe
            AQUI, no segundo em que a recepção olha para o buraco. Uma
            lista de espera que exige ir procurar em outra tela é uma
            lista que ninguém abre. */}
        {/* Dinheiro do atendimento — cobrar aqui preenche o
            agendamento_id no recebível, que é o que destrava o repasse.
            Só some quando o atendimento nem começou (AGENDADO) e não há
            paciente para cobrar. */}
        {a.paciente_id && !a.status.startsWith('CANCELADO') && (
          <ReceberAtendimento
            agendamentoId={a.id}
            pacienteId={a.paciente_id}
            valorSugerido={a.valor_centavos}
            descricao={a.procedimento ?? 'Atendimento'} />
        )}

        {podeEscrever
          && (a.status.startsWith('CANCELADO') || a.status === 'FALTOU')
          && new Date(a.inicio) > new Date() && (
          <div className="painel__encaixe">
            <p>Este horário está livre de novo.</p>
            <button className="botao" onClick={aoVerEncaixe}>
              Ver quem cabe aqui
            </button>
          </div>
        )}

        {podeEscrever && proximos.length > 0 && (
          <div className="painel__acoes">
            {proximos.map((s) => (
              <button key={s}
                      className={`botao ${
                        s.startsWith('CANCELADO') || s === 'FALTOU' || s === 'REMARCADO'
                          ? 'botao--fantasma' : ''}`}
                      onClick={() => (PEDE_MOTIVO.has(s) ? setPedindo(s) : aoMudarEstado(s))}>
                {ACAO[s] ?? ESTADOS[s].rotulo}
              </button>
            ))}
          </div>
        )}

        {pedindo && (
          <PorQue estado={pedindo}
                  aoCancelar={() => setPedindo(null)}
                  aoConfirmar={(motivo) => { setPedindo(null); aoMudarEstado(pedindo, motivo) }} />
        )}

        {podeEscrever && !['ATENDIDO','FALTOU','CANCELADO_PACIENTE','CANCELADO_CLINICA']
              .includes(a.status) && (
          <button className="botao botao--fantasma painel__mover" onClick={aoMover}>
            Mover horário
          </button>
        )}
      </aside>
    </>
  )
}

/**
 * Pede o porquê antes de fechar um horário.
 *
 * O motivo é OPCIONAL na API e a tela não o torna obrigatório: numa
 * terça movimentada, exigir texto para cancelar faria a recepção
 * escrever "." — o que é pior que vazio, porque parece resposta.
 * O que a tela faz é oferecer as razões que se repetem, em um clique,
 * e deixar o campo livre para o resto.
 */
function PorQue({ estado, aoCancelar, aoConfirmar }: {
  estado: EstadoAgendamento
  aoCancelar(): void
  aoConfirmar(motivo: string): void
}) {
  const [motivo, setMotivo] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    ref.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') aoCancelar() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [aoCancelar])

  /* Razões diferentes por estado: "paciente não avisou" não faz sentido
     quando quem desmarcou foi a clínica. */
  const sugestoes: Record<string, string[]> = {
    CANCELADO_PACIENTE: ['Desmarcou por telefone', 'Desmarcou pelo WhatsApp', 'Ficou doente', 'Imprevisto pessoal'],
    CANCELADO_CLINICA:  ['Profissional indisponível', 'Sala indisponível', 'Feriado ou emenda', 'Erro de marcação'],
    REMARCADO:          ['A pedido do paciente', 'A pedido da clínica', 'Conflito de horário'],
    FALTOU:             ['Não avisou', 'Avisou em cima da hora', 'Não atendeu o contato'],
  }
  const opcoes = sugestoes[estado] ?? []

  return (
    <>
      <div className="painel__fundo" onClick={aoCancelar} />
      <div className="porque" ref={ref} tabIndex={-1}
           role="dialog" aria-modal="true" aria-label="Por que este horário está sendo fechado">
        <h3>{ACAO[estado] ?? ESTADOS[estado].rotulo}</h3>
        <p className="ajuda">
          Por quê? Ajuda a entender depois quantas horas se perdem e por qual razão.
          Dá para deixar em branco.
        </p>

        <div className="porque__chips">
          {opcoes.map((o) => (
            <button key={o} type="button"
                    className={`porque__chip${motivo === o ? ' is-ativa' : ''}`}
                    aria-pressed={motivo === o}
                    onClick={() => setMotivo(motivo === o ? '' : o)}>
              {o}
            </button>
          ))}
        </div>

        <input className="entrada" value={motivo} maxLength={300}
               aria-label="Motivo"
               placeholder="ou escreva o motivo"
               onChange={(e) => setMotivo(e.target.value)} />

        <div className="porque__acoes">
          <button className="botao botao--fantasma" onClick={aoCancelar}>Voltar</button>
          <button className="botao" onClick={() => aoConfirmar(motivo)}>Confirmar</button>
        </div>
      </div>
    </>
  )
}
